import z from "zod";

// =============
// Apple App Store Server Notifications v2
// =============

/* =========================
 * Configuration & Payload Schemas
 * ========================= */

export const ConfigSchema = z.looseObject({
	bundleId: z.string().min(1),
});

export const PayloadSchema = z.looseObject({
	data: z.looseObject({
		bundleId: z.string().min(1),
	}),
});

/* =========================
 * JWS Transaction Info (decoded from signedTransactionInfo)
 * ========================= */

export const JWSTransactionInfoSchema = z.looseObject({
	transactionId: z.string(),
	originalTransactionId: z.string(),
	bundleId: z.string(),
	productId: z.string(),
	purchaseDate: z.number(), // Unix timestamp in milliseconds
	originalPurchaseDate: z.number(),
	quantity: z.number(),
	type: z.string(), // e.g., "Auto-Renewable Subscription"
	inAppOwnershipType: z.string(), // "PURCHASED" or "FAMILY_SHARED"
	signedDate: z.number(),
	// appAccountToken — UUID injected by the client during purchase (installId)
	appAccountToken: z.string().uuid().optional(),
	// Price information
	price: z.number().optional(), // In milliunits (price / 1,000 = dollars)
	currency: z.string().optional(),
	// Environment
	environment: z.enum(["Sandbox", "Production"]).optional(),
});

/* =========================
 * Notification Data Object
 * ========================= */

export const NotificationDataSchema = z.looseObject({
	appAppleId: z.number().optional(),
	bundleId: z.string(),
	bundleVersion: z.string().optional(),
	environment: z.enum(["Sandbox", "Production"]),
	signedTransactionInfo: z.string(), // JWS - needs to be decoded
	signedRenewalInfo: z.string().optional(), // JWS - needs to be decoded
});

/* =========================
 * Notification Types
 * ========================= */

// SUBSCRIBED - initial purchase
export const SubscribedNotificationSchema = z.looseObject({
	notificationType: z.literal("SUBSCRIBED"),
	subtype: z.enum(["INITIAL_BUY", "RESUBSCRIBE"]).optional(),
	notificationUUID: z.string(),
	version: z.string(),
	signedDate: z.number(),
	data: NotificationDataSchema,
});

// DID_RENEW - successful renewal
export const DidRenewNotificationSchema = z.looseObject({
	notificationType: z.literal("DID_RENEW"),
	subtype: z.enum(["BILLING_RECOVERY", ""]).optional(),
	notificationUUID: z.string(),
	version: z.string(),
	signedDate: z.number(),
	data: NotificationDataSchema,
});

// DID_FAIL_TO_RENEW - renewal failed
export const DidFailToRenewNotificationSchema = z.looseObject({
	notificationType: z.literal("DID_FAIL_TO_RENEW"),
	subtype: z.string().optional(),
	notificationUUID: z.string(),
	version: z.string(),
	signedDate: z.number(),
	data: NotificationDataSchema,
});

// EXPIRED - subscription expired
export const ExpiredNotificationSchema = z.looseObject({
	notificationType: z.literal("EXPIRED"),
	subtype: z
		.enum([
			"VOLUNTARY",
			"BILLING_RETRY",
			"PRICE_INCREASE",
			"PRODUCT_NOT_FOR_SALE",
		])
		.optional(),
	notificationUUID: z.string(),
	version: z.string(),
	signedDate: z.number(),
	data: NotificationDataSchema,
});

// REFUND - Apple granted a refund to the customer
export const RefundNotificationSchema = z.looseObject({
	notificationType: z.literal("REFUND"),
	subtype: z.string().optional(),
	notificationUUID: z.string(),
	version: z.string(),
	signedDate: z.number(),
	data: NotificationDataSchema,
});

/* =========================
 * Discriminated Union
 * ========================= */

export const AppleNotificationSchema = z.discriminatedUnion(
	"notificationType",
	[
		SubscribedNotificationSchema,
		DidRenewNotificationSchema,
		DidFailToRenewNotificationSchema,
		ExpiredNotificationSchema,
		RefundNotificationSchema,
	],
);

/* =========================
 * Type Exports
 * ========================= */

export type JWSTransactionInfo = z.infer<typeof JWSTransactionInfoSchema>;
export type SubscribedNotification = z.infer<
	typeof SubscribedNotificationSchema
>;
export type DidRenewNotification = z.infer<typeof DidRenewNotificationSchema>;
export type RefundNotification = z.infer<typeof RefundNotificationSchema>;
export type AppleNotification = z.infer<typeof AppleNotificationSchema>;
